import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../../../theme/styles';
import { InputLabel, MenuItem, Select, FormControl } from '@mui/material';
import testAheadService from '../../../services/TestAheadService';
import { TestAheadCourseDTO } from '@beable/roster-backend-client-v1';
import { useCourseState, useCourseDispatch, CourseActionType } from '../../../context/CourseContext';

interface ProvisionSelectProps {
  courseId?: string;
  onChange: any;
  disabled?: boolean;
  variant?: 'filled' | 'outlined';
  className?: string;
  inPanel?: boolean;
}

export default function TestAheadCourseSelector({ courseId, onChange, disabled, variant, className, inPanel }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { testAheadCourseSaved, site } = useCourseState();

  const [courses, setCourses] = useState<TestAheadCourseDTO[]>([]);
  const [displayCourses, setDisplayCourses] = useState<TestAheadCourseDTO[]>([]);
  const [courseTitles, setCourseTitles] = useState<string[]>([]);
  const [selectedCourseCode, setSelectedCourseCode] = useState<string| undefined>(undefined);
  const [selectedCourse, setSelectedCourse] = useState<TestAheadCourseDTO | undefined>(undefined)
  const memoOnChange = useCallback(onChange, []);
  const courseDispatch = useCourseDispatch();

  
  useEffect(() => {
    const fetchCourses = async () => {
      const data = await testAheadService.getTestAheadCourses();
      if (data) {
        setCourses(data);
        
        const titles = Array.from(new Set(data.map(course => course.courseCode)));
        setCourseTitles(titles);
        if(titles.length === 1) {
          setSelectedCourseCode(titles[0]);
        }
        
        if(data.length === 1) {
          memoOnChange(data[0]);
        }
      } else {
        setCourses([]);
      }
    };

    const resetState = () => {
      courseDispatch({
        type: CourseActionType.TestAheadCourseSaved,
        testAheadCourseSaved: false,
      })
    }
    fetchCourses();
    resetState();

  }, [memoOnChange, site, testAheadCourseSaved, courseDispatch]);

  useEffect(() => {
    if (courseId) {
      const course = courses.find((course) => course.id === courseId);
      setSelectedCourse(course);
      memoOnChange(course);
    } else {
      setSelectedCourse(undefined);
      memoOnChange(undefined);
    }
  }, [courseId, memoOnChange, courses]);


  useEffect(() => {
    if (selectedCourseCode) {
      setDisplayCourses(
        courses &&
          courses.filter((course) => course.courseCode === selectedCourseCode)
      );
      if(selectedCourse && selectedCourse.courseCode !== selectedCourseCode) {
        setSelectedCourse(undefined);
        memoOnChange(undefined);
      }
    } else if (courseId && !selectedCourse) {
      const courseCode = courses.find(course => course.id === courseId)?.courseCode;
      setSelectedCourseCode(courseCode);
      setDisplayCourses(courses &&
        courses.filter((course) => course.courseCode === courseCode))
    } else {
      setSelectedCourseCode(undefined);
      setDisplayCourses([]);
    }
    
  }, [selectedCourseCode, courses, memoOnChange, selectedCourse, courseId]);



  const handleCourseChange = (event: any) => {
    const selected = courses.find((course) => course.id === event.target.value);
    setSelectedCourse(selected)
    onChange(selected);
  }

  const handleSelectedCourseCodeChange = (event: any) => {
    setSelectedCourseCode(event.target.value);
  }

  return (
    <div>
      <FormControl
        className={className}
        sx={inPanel ? styles.pFormControlPanel : styles.pFormControl}
        variant={ variant || "standard" }
      >
        <InputLabel>
          { courses && courses.length ? "Select Course": "No Courses Found"}
        </InputLabel>
        <Select
          variant={ variant || "standard" }
          data-testid="test-courseCode"
          value={selectedCourseCode || ''}
          onChange={handleSelectedCourseCodeChange}
          name="courseCode"
          id="courseCode"
          role="option"
          disabled={!courseTitles || courseTitles.length <= 1 || disabled}>
          {courseTitles.length &&
            courseTitles.map((title: any) => (
              <MenuItem key={title} value={title} role="option">
                {title}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl
        className={className}
        sx={inPanel ? styles.pFormControlPanel : styles.pFormControl}
        variant={ variant || "standard" }
      >
        <InputLabel>
          { displayCourses && displayCourses.length ? "Select Category" : "No Categories Found" }
        </InputLabel>
        <Select
          variant="standard"
          value={selectedCourse?.id || ''}
          onChange={handleCourseChange}
          name="courseId"
          id="courseId"
          data-testid="test-courseId"
          role="option"
          disabled={!displayCourses || displayCourses.length <= 1 || disabled}>
          {displayCourses.length &&
            displayCourses.map((course: any) => (
              <MenuItem key={course.id} value={course.id} role="option">
                {course.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </div>
  );
}
